import { useTranslation } from 'react-i18next';
import React, { Dispatch, SetStateAction } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from '@mui/material';

import CloseIcon from 'app/components/Icons/CloseIcon';
import StarIcon from 'app/components/Icons/StarIcon';
import useCsiModal from 'app/components/CsiModal/useCsiModal';
import { useFormCsi } from 'app/providers/csi/hooks/useFormCsi';
import { useCustomNavigate } from 'app/utils/customHooks/useCustomNavigate';

import {
  closeBtn,
  dialogActions,
  dialogContent,
  dialogTitle,
  paperProps,
  rateBox,
  rateTypo,
  starBox,
  textField,
  textFieldTypo,
} from './CsiModal.sx';

interface Props {
  open: boolean;
  showText?: boolean;
  close: Dispatch<SetStateAction<boolean>>;
  approveAction: (rating: number, comment: string) => void;
}

const CsiModal = (props: Props) => {
  const { open, showText, close, approveAction } = props;
  const { t } = useTranslation();
  const navigate = useCustomNavigate();
  const {
    starIndex,
    setStarIndex,
    setValue,
    value,
    handleChange,
    rates,
    setStarIndexHover,
    starIndexHover,
  } = useCsiModal();
  const formCsi = useFormCsi();

  const handleClean = () => {
    setStarIndex(0);
    setValue('');
  };

  const handleClose = () => {
    close(false);
    formCsi.close();
    navigate(`${formCsi.redirectPage}`);
    handleClean();
  };

  return (
    <Dialog
      open={formCsi.isOpen}
      PaperProps={{
        sx: paperProps,
      }}
      onClose={handleClose}
    >
      <DialogTitle sx={{ ...dialogTitle, mb: '12px' }}>
        <Typography textAlign='center' fontWeight={600} fontSize={24}>
          {t('csi.evaluateProcess')}
        </Typography>
        <Button color={'tertiary'} onClick={handleClose} sx={closeBtn}>
          <CloseIcon />
        </Button>
      </DialogTitle>
      <DialogContent sx={dialogContent}>
        {showText && (
          <Typography sx={{ maxWidth: '370px' }} fontWeight={400} fontSize={16}>
            {t('csi.shareExperience')}
          </Typography>
        )}
        <Box onMouseLeave={() => setStarIndexHover(0)} sx={rateBox}>
          {rates.map((rate, index) => (
            <Box
              sx={starBox}
              onMouseEnter={() => {
                setStarIndexHover(index + 1);
              }}
              onClick={() => {
                setStarIndex(index + 1);
              }}
              key={index}
            >
              <StarIcon
                color={index > starIndex - 1 && index > starIndexHover - 1 ? 'secondary' : null}
                width={28}
              />
              {((index === starIndex - 1 && starIndexHover === 0) ||
                index === starIndexHover - 1) && (
                <Typography sx={rateTypo}>{t(`csi.${rate}`)}</Typography>
              )}
            </Box>
          ))}
        </Box>

        <TextField
          sx={textField}
          inputProps={{
            maxLength: 100,
          }}
          multiline
          rows={4}
          variant='outlined'
          placeholder='Qeydiniz'
          value={value}
          onChange={handleChange}
        />
        <Typography sx={{ ...textFieldTypo, color: value.length === 100 ? 'red' : '#14191F66' }}>
          {value.length}/100
        </Typography>
      </DialogContent>
      <DialogActions sx={dialogActions}>
        <Button
          disabled={starIndex === 0}
          sx={{ width: '100%' }}
          onClick={() => {
            formCsi.saveRating(starIndex, value);
            approveAction(starIndex, value);
            handleClean();
          }}
        >
          {t('csi.confirm')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CsiModal;
